import * as React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Divider from 'antd/lib/divider';
import Collapse, { CollapseProps } from 'antd/lib/collapse';
import Text from 'antd/lib/typography/Text';
import { AppRouteComponentMatchParams, WorkflowRunComponentProps } from '@/typescriptHelpers';
import { CaretRightOutlined } from '@ant-design/icons';
import GoToReleaseCTA from '../appHome/GoToReleaseCTA';
import { createReleaseOverviewRoute } from '@/helpers/routeHelpers';
import { useParams } from 'react-router-dom';
import ConfigProvider from 'antd/lib/config-provider';

export interface WorkflowRunOverviewCardProps extends WorkflowRunComponentProps {
    releaseId: string;
    displayableVersion: string;
    backgroundColor?: string;
    headerBackgroundColor?: string;
    borderColor?: string;
    headerComponent: React.ReactNode;
    contentTopline: React.ReactNode;
    contentComponent: React.ReactNode;
    sideComponent: React.ReactNode;
    shouldStartCollapsed?: boolean;
}

export const WorkflowRunOverviewCard: React.FC<WorkflowRunOverviewCardProps> = ({
    releaseId,
    displayableVersion,
    backgroundColor = runwayColors.white,
    headerBackgroundColor = runwayColors.white,
    borderColor = antGrey.grey5,
    headerComponent,
    contentTopline,
    contentComponent,
    sideComponent,
}) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();
    const releasePath = createReleaseOverviewRoute(orgIdParam, appIdParam, releaseId);

    const collapseItems: CollapseProps['items'] = [
        {
            key: '1',
            label: (
                <div
                    style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: 17,
                    }}
                >
                    <Text
                        style={{
                            verticalAlign: 'middle',
                            alignSelf: 'start',
                            fontSize: 16,
                            color: antGrey.grey8,
                            flexShrink: 0,
                        }}
                    >
                        {displayableVersion}
                    </Text>
                    {headerComponent}
                </div>
            ),
            children: (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        height: '100%',
                        width: '100%',
                        gap: 16,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        {contentTopline}
                        <GoToReleaseCTA releasePath={releasePath} />
                    </div>
                    <Divider
                        style={{
                            marginTop: '0px',
                            marginBottom: '2px',
                            marginLeft: '0px',
                            marginRight: '0px',
                        }}
                    />
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'stretch',
                            height: '100%',
                            width: '100%',
                            gap: 26,
                        }}
                    >
                        <div
                            style={{
                                height: '100%',
                                width: 290,
                                display: 'flex',
                                flexShrink: 0,
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                alignItems: 'stretch',
                            }}
                        >
                            {sideComponent}
                        </div>
                        <div
                            style={{
                                height: '100%',
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'flex-start',
                                alignItems: 'stretch',
                                width: '80%',
                            }}
                        >
                            {contentComponent}
                        </div>
                    </div>
                </div>
            ),
            style: {
                borderRadius: 12,
            },
            forceRender: true,
        },
    ];

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        headerPadding: '20px 26px',
                        contentPadding: '24px 26px',
                        headerBg: headerBackgroundColor,
                        contentBg: backgroundColor,
                        colorBorder: borderColor,
                        colorBorderBg: borderColor,
                        borderRadiusLG: 12,
                        lineWidth: 1,
                        lineType: 'solid',
                        borderRadius: 12,
                    },
                    Popover: {
                        borderRadiusLG: 8,
                        boxShadowSecondary: 'var(--rw-shadow-lg)',
                    },
                },
                token: {
                    colorBorder: borderColor,
                    colorBorderBg: borderColor,
                },
            }}
        >
            <Collapse
                defaultActiveKey={'1'}
                className="run-overview-card"
                style={{
                    maxWidth: 1600,
                    minWidth: 1000,
                    borderRadius: 12,
                    marginBottom: 24,
                    padding: 0,
                }}
                expandIcon={({ isActive }) => (
                    <CaretRightOutlined
                        style={{
                            marginTop: '3px',
                            color: antGrey.grey6,
                            fontSize: 24,
                        }}
                        rotate={isActive ? 90 : 0}
                    />
                )}
                items={collapseItems}
                destroyInactivePanel={false}
            />
        </ConfigProvider>
    );
};
